import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPromoteSeoV1HeadlessSitemapEntry from './promote-seo-v1-headless-sitemap-entry-seo-sitemap-entries.http.js';

/** Sitemap entry containing page information */
export interface SitemapEntry {
  /**
   * Entity identifier (e.g., productId, pageId)
   * @minLength 1
   * @maxLength 255
   */
  _id?: string;
  /** Indicates if the entry should be included in the generated sitemap. */
  shouldBeIncludedInSitemap?: boolean;
  /**
   * Optional last modification date in ISO 8601 format
   * @maxLength 50
   */
  _updatedDate?: string;
  /**
   * Optional list of images
   * @maxSize 1000
   */
  images?: Image[];
  /**
   * Optional list of video data
   * @maxSize 100
   */
  videos?: Video[];
}

/** Image information for sitemap entries */
export interface Image {
  /**
   * Image URL
   * @format WEB_URL
   * @maxLength 2048
   * @minLength 1
   */
  url?: string;
  /**
   * Optional image alt text
   * @maxLength 2048
   */
  altText?: string;
}

/** Video information for sitemap entries */
export interface Video {
  /**
   * Video content location URL
   * @format WEB_URL
   * @maxLength 2048
   * @minLength 1
   */
  url?: string;
  /**
   * Video thumbnail location URL
   * @format WEB_URL
   * @maxLength 2048
   * @minLength 1
   */
  thumbnailUrl?: string;
  /**
   * Optional video title
   * @maxLength 255
   */
  title?: string;
  /**
   * Optional video description
   * @maxLength 1000
   */
  description?: string;
}

/** Request message for getting sitemap pages */
export interface ListSitemapPagesRequest {
  /** Item type identifier - must be a valid enum value */
  itemType?: ItemTypeWithLiterals;
  /** Optional cursor for pagination */
  paging?: CursorPaging;
}

/** Enum representing different types of items that can have sitemap entries */
export enum ItemType {
  /** Stores products */
  STORES_PRODUCT = 'STORES_PRODUCT',
  /** Blog posts */
  BLOG_POST = 'BLOG_POST',
  /** Blog categories */
  BLOG_CATEGORY = 'BLOG_CATEGORY',
  /** Blog tags */
  BLOG_TAGS = 'BLOG_TAGS',
  /** Blog archives */
  BLOG_ARCHIVE = 'BLOG_ARCHIVE',
  /** Forum posts */
  FORUM_POST = 'FORUM_POST',
  /** Forum categories */
  FORUM_CATEGORY = 'FORUM_CATEGORY',
  /** Groups posts */
  GROUPS_POST = 'GROUPS_POST',
  /** Groups pages */
  GROUPS_PAGE = 'GROUPS_PAGE',
  /** Events pages */
  EVENTS_PAGE = 'EVENTS_PAGE',
  /** Bookings services */
  BOOKINGS_SERVICE = 'BOOKINGS_SERVICE',
  /** Members area profiles */
  MEMBERS_AREA_PROFILE = 'MEMBERS_AREA_PROFILE',
  /** Challenges/online programs pages */
  CHALLENGES_PAGE = 'CHALLENGES_PAGE',
  /** Store categories */
  STORES_CATEGORY = 'STORES_CATEGORY',
  /** Store sub-categories */
  STORES_SUB_CATEGORY = 'STORES_SUB_CATEGORY',
  /** Portfolio collections */
  PORTFOLIO_COLLECTIONS = 'PORTFOLIO_COLLECTIONS',
  /** Portfolio projects */
  PORTFOLIO_PROJECTS = 'PORTFOLIO_PROJECTS',
  /** Pricing plans */
  PRICING_PLANS = 'PRICING_PLANS',
  /** Restaurant menu pages */
  RESTAURANTS_MENU_PAGE = 'RESTAURANTS_MENU_PAGE',
}

/** @enumType */
export type ItemTypeWithLiterals =
  | ItemType
  | 'STORES_PRODUCT'
  | 'BLOG_POST'
  | 'BLOG_CATEGORY'
  | 'BLOG_TAGS'
  | 'BLOG_ARCHIVE'
  | 'FORUM_POST'
  | 'FORUM_CATEGORY'
  | 'GROUPS_POST'
  | 'GROUPS_PAGE'
  | 'EVENTS_PAGE'
  | 'BOOKINGS_SERVICE'
  | 'MEMBERS_AREA_PROFILE'
  | 'CHALLENGES_PAGE'
  | 'STORES_CATEGORY'
  | 'STORES_SUB_CATEGORY'
  | 'PORTFOLIO_COLLECTIONS'
  | 'PORTFOLIO_PROJECTS'
  | 'PRICING_PLANS'
  | 'RESTAURANTS_MENU_PAGE';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 200
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

/** Response message for getting sitemap pages */
export interface ListSitemapPagesResponse {
  /** List of sitemap entries */
  pages?: SitemapEntry[];
  /** Paging metadata for next page of results */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

/** Request message for getting static sitemap pages */
export interface ListStaticSitemapPagesRequest {
  /** Optional cursor for pagination */
  paging?: CursorPaging;
}

/** Response message for getting sitemap pages */
export interface ListStaticSitemapPagesResponse {
  /** List of sitemap entries */
  pages?: SitemapEntry[];
  /** Paging metadata for next page of results */
  pagingMetadata?: PagingMetadataV2;
}

/**
 * Lists sitemap pages for a given item type with optional pagination
 * @public
 * @permissionId WIX.SEO_SITEMAP_ENTRIES_READ
 * @applicableIdentity APP
 * @returns Response message for getting sitemap pages
 * @fqn wix.promote.seo.headless.sitemap.service.v1.SeoHeadlessSitemapService.ListSitemapPages
 */
export async function listSitemapPages(
  options?: ListSitemapPagesOptions
): Promise<
  NonNullablePaths<
    ListSitemapPagesResponse,
    | `pages`
    | `pages.${number}._id`
    | `pages.${number}.shouldBeIncludedInSitemap`
    | `pages.${number}._updatedDate`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    itemType: options?.itemType,
    paging: options?.paging,
  });

  const reqOpts =
    ambassadorWixPromoteSeoV1HeadlessSitemapEntry.listSitemapPages(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          itemType: '$[0].itemType',
          paging: '$[0].paging',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListSitemapPagesOptions {
  /** Item type identifier - must be a valid enum value */
  itemType?: ItemTypeWithLiterals;
  /** Optional cursor for pagination */
  paging?: CursorPaging;
}
